import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Size, TopActionBar, Variant, RefreshIcon } from '@defa/defa-component-library';
import i18n from '@i18n';

const BANNER_EVENT = 'apollo:banner';
const BANNER_CLEAR_EVENT = 'apollo:banner-clear';

type BannerLevel = 'info' | 'warning' | 'error';

type Banner = {
    header: string;
    description: string;
    level?: BannerLevel;
    onRetry?: () => void;
} | null;

type Ctx = {
    banner: Banner;
    show: (b: NonNullable<Banner>) => void;
    clear: () => void;
};

const TopBannerContext = createContext<Ctx>({
    banner: null,
    show: () => {},
    clear: () => {},
});

export const TopBannerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [banner, setBanner] = useState<Banner>(null);

    const show = useCallback((b: NonNullable<Banner>) => setBanner(b), []);
    const clear = useCallback(() => setBanner(null), []);

    // 🔌 Listen for apollo banner events emitted from the Apollo layer
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const onBanner = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                header?: string;
                description: string;
                level?: BannerLevel;
                onRetry?: () => void;
            };

            // Provide a sensible default header if none given
            show({
                header: detail.header ?? 'We’re having trouble right now',
                description: detail.description,
                level: detail.level,
                onRetry: detail.onRetry,
            });
        };

        const onClear = () => clear();

        window.addEventListener(BANNER_EVENT, onBanner as EventListener);
        window.addEventListener(BANNER_CLEAR_EVENT, onClear as EventListener);

        return () => {
            window.removeEventListener(BANNER_EVENT, onBanner as EventListener);
            window.removeEventListener(BANNER_CLEAR_EVENT, onClear as EventListener);
        };
    }, [show, clear]);

    const value = useMemo(() => ({ banner, show, clear }), [banner, show, clear]);

    return <TopBannerContext.Provider value={value}>{children}</TopBannerContext.Provider>;
};

const useTopBanner = () => useContext(TopBannerContext);

export const TopBannerOutlet: React.FC = () => {
    const { banner, clear } = useTopBanner();
    if (!banner) return null;

    // Optionally map level -> TopActionBar props (keep your current visuals if you like)
    // Example: actionBarType could be 'info' | 'warning' | 'error' if supported.
    const actionBarType = 'action' as const;

    return (
        <TopActionBar
            header={banner.header}
            description={banner.description}
            actionBarType={actionBarType}
        >
            {banner.onRetry && (
                <Button
                    name="retry-top-banner"
                    variant={Variant.GRAYSCALE}
                    size={Size.SMALL}
                    icon={RefreshIcon}
                    onClick={() => {
                        banner.onRetry?.();
                        clear();
                    }}
                    text={i18n.t('Retry')}
                    fillParent={false}
                />
            )}
        </TopActionBar>
    );
};

export const notifyBanner = (detail: {
    header?: string;
    description: string;
    level?: BannerLevel;
    onRetry?: () => void;
}) => {
    try {
        window.dispatchEvent(new CustomEvent(BANNER_EVENT, { detail }));
    } catch {
        // no-op
    }
};

export const clearBanner = () => {
    try {
        window.dispatchEvent(new CustomEvent(BANNER_CLEAR_EVENT));
    } catch {
        // no-op
    }
};
